import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useEditMode } from "./hooks/useEditMode";
import CompanyDetailsCard from "./tabs/CompanyDetailsCard";
import ContactDetailsCard from "./tabs/ContactDetailsCard";
import ContractDetailsCard from "./tabs/ContractDetailsCard";
import ImportSourceCard from "./tabs/ImportSourceCard";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/v2/alert-dialog";
import { ROUTES } from "@/constants/routes";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import { useClients } from "@/lib/hooks/useClients";

const EditClientPage = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { t } = useTranslation();
  const { client, clientLoading, clientError } = useClients();
  const { showConfirmDialog, confirmDiscard, cancelDiscard } = useEditMode();

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.clients"), href: `/${ROUTES.clients}` },
      {
        label: client?.name || t("common.client"),
        href: `/${ROUTES.clients}/${clientId}`,
      },
      { label: t("admin.client.editClient") },
    ],
  });

  // Early returns after all hooks
  if (clientLoading) {
    return (
      <ModernPageLayout breadcrumbs={breadcrumbs}>
        <div className="flex h-full w-full items-center justify-center p-6">
          <Loader2 className="size-16 animate-spin text-muted-foreground" />
        </div>
      </ModernPageLayout>
    );
  }

  if (clientError?.status === 404 || !client) {
    return (
      <ModernPageLayout breadcrumbs={breadcrumbs}>
        <div className="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          {t("errors.invalidEntity", { entity: t("common.client") })}
        </div>
      </ModernPageLayout>
    );
  }

  if (!clientId) {
    return (
      <ModernPageLayout breadcrumbs={breadcrumbs}>
        <div className="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          {t("errors.pageNotFound")}
        </div>
      </ModernPageLayout>
    );
  }

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      title={`${t("admin.client.editClient")} - ${client?.name}`}
    >
      <div className="gap-4 flex flex-col">
        <CompanyDetailsCard client={client} clientId={clientId} />
        <ContactDetailsCard client={client} clientId={clientId} />
        <ImportSourceCard
          client={client}
          clientId={clientId}
        />
        <ContractDetailsCard
          client={client}
          clientId={clientId}
          defaultPaymentType={client?.paymentType}
        />
      </div>

      <AlertDialog open={showConfirmDialog} onOpenChange={cancelDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("prompts.unsavedChanges.title", "Unsaved Changes")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "prompts.unsavedChanges.description",
                "You have unsaved changes. Do you want to discard them?"
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDiscard}>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard}>
              {t("prompts.unsavedChanges.leave", "Discard Changes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ModernPageLayout>
  );
};

export default EditClientPage;
